
/* ════════════════════════════════════════════════════════════════════════
   Per-clinic feature control — the client side.

   The backend owns the decision (Clinic.features, Super Admin → Feature
   control). This module answers "may this clinic use X?" everywhere in the
   app, including from places that cannot wait for a fetch — a route guard has
   to decide synchronously while React Router is resolving the URL.

   How that works: the flags are fetched once per session by
   ClinicFeatureSync and cached in whichever storage holds the session (the
   Remember-Me split, same as authUtils). Every reader goes through
   getClinicFeatures(), which falls back to defaults when nothing is cached
   yet. Defaults reproduce today's behaviour exactly — viewer on, measurement
   off — so a first paint before the fetch resolves never takes the viewer away
   from a clinic that has it.
   ════════════════════════════════════════════════════════════════════════ */

export interface ClinicFeatures {
  /** Full OHIF DICOM viewer (`/viewer`). Express lite is NOT gated by this. */
  dicomViewer: boolean;
  /** Measurement module: `/measurement`, the worksheet, Echo Measurements tabs. */
  measurement: boolean;
}

export const CLINIC_FEATURES_STORAGE_KEY = 'clinicFeatures';

/**
 * What the app assumes before it has heard from the server.
 *
 * dicomViewer true: every clinic has always had the viewer, and a cold cache
 * must not withdraw it. measurement false: unvalidated module, opt-in only.
 */
export const DEFAULT_CLINIC_FEATURES: ClinicFeatures = {
  dicomViewer: true,
  measurement: false,
};

const coerce = (raw: any): ClinicFeatures => ({
  dicomViewer:
    typeof raw?.dicomViewer === 'boolean' ? raw.dicomViewer : DEFAULT_CLINIC_FEATURES.dicomViewer,
  measurement:
    typeof raw?.measurement === 'boolean' ? raw.measurement : DEFAULT_CLINIC_FEATURES.measurement,
});

/** Session storage first, then localStorage — mirrors getToken()'s Remember-Me split. */
const readRaw = (): string | null => {
  try {
    return (
      sessionStorage.getItem(CLINIC_FEATURES_STORAGE_KEY) ||
      localStorage.getItem(CLINIC_FEATURES_STORAGE_KEY)
    );
  } catch {
    // Private-mode / disabled storage: fall through to defaults rather than throw.
    return null;
  }
};

/** The clinic's flags as last known, or the defaults. Never throws. */
export const getClinicFeatures = (): ClinicFeatures => {
  const raw = readRaw();
  if (!raw) {
    return { ...DEFAULT_CLINIC_FEATURES };
  }
  try {
    return coerce(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CLINIC_FEATURES };
  }
};

/**
 * Cache the flags the server just returned.
 *
 * Written to whichever storage holds the current session so that a Remember-Me
 * login keeps them across a browser restart and a non-remembered one does not.
 */
export const storeClinicFeatures = (features: Partial<ClinicFeatures> | null | undefined) => {
  if (!features) {
    return;
  }
  try {
    const store = localStorage.getItem('token') !== null ? localStorage : sessionStorage;
    store.setItem(CLINIC_FEATURES_STORAGE_KEY, JSON.stringify(coerce(features)));
  } catch {
    /* storage unavailable — the in-memory Redux copy still drives this session */
  }
};

/**
 * Is the DICOM viewer available to the logged-in user's clinic?
 *
 * Express lite deliberately stays available when this is false: a lite-only
 * clinic still reviews clips and writes reports, which is the entire point of
 * being able to switch the viewer off.
 */
export const isDicomViewerEnabled = (features?: ClinicFeatures): boolean =>
  (features || getClinicFeatures()).dicomViewer === true;

/**
 * Is the measurement module available? Decided per clinic by Super Admin → Feature control.
 */
export const isMeasurementEnabled = (features?: ClinicFeatures): boolean =>
  (features || getClinicFeatures()).measurement === true;
